import { Link as RouterLink } from 'react-router-dom';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { alpha } from '@mui/material/styles';
import { Box, Button, Card, CardContent, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import SectionHeading from '../../components/Common/SectionHeading';
import {
  companyStats,
  homeBottomBenefits,
  homePreviewRangeIds,
  pageContent,
  productRanges,
  whyUs,
} from '../../data/siteData';

function HomePage() {
  const content = pageContent.home;
  const previewRanges = productRanges.filter((range) => homePreviewRangeIds.includes(range.id));

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background:
            'radial-gradient(circle at 50% 20%, rgba(74,14,143,0.45) 0%, rgba(26,5,51,1) 45%, rgba(13,1,32,1) 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(201,168,76,0.07) 0px, rgba(201,168,76,0.07) 1px, transparent 1px, transparent 32px)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 8, md: 10 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Stack spacing={3}>
                <Chip
                  label={content.heroChip}
                  sx={{
                    width: 'fit-content',
                    bgcolor: alpha('#c9a84c', 0.12),
                    color: '#f4dc97',
                    border: '1px solid rgba(201,168,76,0.24)',
                  }}
                />
                <Typography variant="h1" sx={{ color: '#ffffff', fontSize: { xs: '3.2rem', md: '5.2rem' }, lineHeight: 0.95 }}>
                  {content.heroTitle}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.76)', maxWidth: 680, lineHeight: 1.8 }}>
                  {content.heroDescription}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    component={RouterLink}
                    to={content.heroPrimaryCta.path}
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowOutwardRoundedIcon />}
                  >
                    {content.heroPrimaryCta.label}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={content.heroSecondaryCta.path}
                    variant="outlined"
                    sx={{
                      color: '#ffffff',
                      borderColor: 'rgba(255,255,255,0.64)',
                      '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    {content.heroSecondaryCta.label}
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  bgcolor: 'rgba(255,255,255,0.08)',
                  color: '#ffffff',
                  border: '1px solid rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(18px)',
                }}
              >
                <Typography variant="h4" sx={{ color: '#ffffff', mb: 3 }}>
                  Growth Snapshot
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 2,
                  }}
                >
                  {companyStats.map((stat) => (
                    <Box
                      key={stat.label}
                      sx={{
                        p: 2.25,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(201,168,76,0.18)',
                      }}
                    >
                      <Typography variant="h4" sx={{ color: '#f4dc97', mb: 0.5 }}>
                        {stat.value}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>{stat.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: '#1a0533', py: { xs: 6, md: 7 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={content.whySection.eyebrow}
            title={content.whySection.title}
            subtitle={content.whySection.subtitle}
            light
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {whyUs.map((item) => {
              const Icon = item.icon;
              return (
                <Paper key={item.title} elevation={0} sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.06)', color: '#ffffff' }}>
                  <Icon sx={{ color: '#c9a84c', fontSize: 34 }} />
                  <Typography variant="h4" sx={{ color: '#ffffff', mt: 2, mb: 1 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>{item.description}</Typography>
                </Paper>
              );
            })}
          </Box>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow={content.previewSection.eyebrow}
            title={content.previewSection.title}
            subtitle={content.previewSection.subtitle}
          />
          <Grid container spacing={3}>
            {previewRanges.map((range) => {
              const Icon = range.icon;
              return (
                <Grid item xs={12} md={4} key={range.id}>
                  <Card elevation={0} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 62,
                          height: 62,
                          borderRadius: 3.5,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: range.accent,
                          color: range.color,
                          mb: 2.5,
                        }}
                      >
                        <Icon />
                      </Box>
                      <Typography variant="h4" sx={{ mb: 1.25 }}>
                        {range.name}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                        {range.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Stack alignItems="center" sx={{ mt: 4 }}>
            <Button component={RouterLink} to={content.previewSection.cta.path} variant="outlined" color="secondary">
              {content.previewSection.cta.label}
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: { xs: 4, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            color: '#ffffff',
            background: 'linear-gradient(135deg, #4a0e8f 0%, #1a0533 100%)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top right, rgba(201,168,76,0.16), transparent 36%)',
            }}
          />
          <Grid container spacing={4} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h3" sx={{ color: '#ffffff', mb: 1.2 }}>
                {content.bottomCta.title}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.74)' }}>{content.bottomCta.subtitle}</Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={1.4} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                {homeBottomBenefits.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Stack key={item.title} direction="row" spacing={1.25} alignItems="center">
                      <Icon sx={{ color: '#c9a84c' }} />
                      <Typography sx={{ color: '#ffffff' }}>{item.title}</Typography>
                    </Stack>
                  );
                })}
                <Button component={RouterLink} to={content.bottomCta.path} variant="contained" color="primary" sx={{ mt: 1 }}>
                  {content.bottomCta.buttonLabel}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}

export default HomePage;
